/**
 * Phone Authentication Provider Interface
 *
 * Defines the interface for Firebase Phone Auth SMS verification.
 */

/**
 * Main provider interface that all SMS providers must implement
 */
export interface PhoneAuthProvider {
  /**
   * Initialize the provider (e.g., setup reCAPTCHA)
   * Should be called before sending verification codes
   */
  initialize(): Promise<void>;

  /**
   * Send a verification code to the specified phone number
   * @param phoneNumber - Phone number in E.164 format (e.g., +6591234567)
   * @returns Session information for code verification
   */
  sendVerificationCode(phoneNumber: string): Promise<PhoneAuthSession>;

  /**
   * Verify the code entered by the user
   * @param session - Session returned from sendVerificationCode
   * @param code - 6-digit verification code entered by user
   * @returns Verification result with success status
   */
  verifyCode(session: PhoneAuthSession, code: string): Promise<PhoneAuthResult>;

  /**
   * Clean up resources (e.g., remove reCAPTCHA widget)
   * Should be called when provider is no longer needed
   */
  dispose(): Promise<void>;

  /**
   * Name of the provider
   */
  readonly providerName: 'firebase';
}

/**
 * Session information for SMS verification
 */
export interface PhoneAuthSession {
  /** Unique session identifier */
  sessionId: string;

  /** Phone number being verified (E.164 format) */
  phoneNumber: string;

  /** Provider used for this session */
  provider: 'firebase';

  /** When this session expires (typically 5 minutes from creation) */
  expiresAt: Date;

  /** Confirmation result from signInWithPhoneNumber - has confirm() method */
  confirmationResult?: any; // React Native Firebase confirmation result
}

/**
 * Result of code verification
 */
export interface PhoneAuthResult {
  /** Whether verification was successful */
  success: boolean;

  /** Error message if verification failed */
  error?: string;
}

/**
 * Error codes that can be thrown by providers
 */
export enum PhoneAuthErrorCode {
  // Common errors
  INVALID_PHONE_NUMBER = 'invalid-phone-number',
  CODE_EXPIRED = 'code-expired',
  INVALID_CODE = 'invalid-code',
  TOO_MANY_ATTEMPTS = 'too-many-attempts',
  TOO_MANY_REQUESTS = 'too-many-requests',

  // Firebase-specific
  RECAPTCHA_FAILED = 'recaptcha-failed',
  QUOTA_EXCEEDED = 'quota-exceeded',

  // Generic
  NETWORK_ERROR = 'network-error',
  UNKNOWN_ERROR = 'unknown-error',
}

/**
 * Custom error class for phone auth errors
 */
export class PhoneAuthError extends Error {
  constructor(
    public code: PhoneAuthErrorCode,
    message: string,
    public originalError?: any
  ) {
    super(message);
    this.name = 'PhoneAuthError';
  }
}
